import { RefreshCw } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import type { BalanceSheet } from '../../types/drawdown';

export function StatementOfFinancialPosition({
  balanceSheet,
  netIncome,
  isLoading,
}: {
  balanceSheet: BalanceSheet | null | undefined;
  netIncome: number;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="border rounded-lg bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            STATEMENT OF FINANCIAL POSITION (BALANCE SHEET)
          </h2>
        </div>
        <div className="p-6">
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            Loading balance sheet...
          </div>
        </div>
      </div>
    );
  }

  if (!balanceSheet) {
    return (
      <div className="border rounded-lg bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            STATEMENT OF FINANCIAL POSITION (BALANCE SHEET)
          </h2>
        </div>
        <div className="p-6">
          <div className="text-center py-8 text-muted-foreground">
            No balance sheet data
          </div>
        </div>
      </div>
    );
  }

  const assetAccounts = balanceSheet.assets;
  const liabilityAccounts = balanceSheet.liabilities;
  const equityAccounts = balanceSheet.equity;

  // The API serializes Decimal totals as JSON strings; coerce to numbers so the
  // additions below add instead of string-concatenating.
  const totalLiabilities = Number(balanceSheet.totals.total_liabilities);
  const totalEquity = Number(balanceSheet.totals.total_equity);

  // Net income belongs to the equity side (current-year retained earnings), so
  // it is added into equity and into the combined liabilities & equity total.
  const totalEquityWithIncome = totalEquity + netIncome;
  const totalLiabilitiesAndEquity = totalLiabilities + totalEquityWithIncome;

  return (
    <div className="border rounded-lg bg-card">
      <div className="border-b px-6 py-4">
        <h2 className="text-lg font-semibold">
          STATEMENT OF FINANCIAL POSITION (BALANCE SHEET)
        </h2>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 gap-8">
          {/* Left Column - Assets */}
          <div>
            <h3 className="font-semibold text-foreground mb-3">ASSETS</h3>
            <div className="space-y-2">
              {assetAccounts.map((account) => (
                <div
                  key={account.account_code}
                  className="flex justify-between text-sm"
                >
                  <span className="text-muted-foreground">
                    {account.account_name}
                  </span>
                  <span>{formatCurrency(account.net_balance)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Prepayment Pool</span>
                <span>
                  {formatCurrency(balanceSheet.prepayment_pool_balance ?? 0)}
                </span>
              </div>
            </div>
            <div className="flex justify-between font-semibold mt-4 pt-2 border-t">
              <span>Total Assets</span>
              <span>{formatCurrency(balanceSheet.totals.total_assets)}</span>
            </div>
          </div>

          {/* Right Column - Liabilities & Equity */}
          <div className="space-y-6">
            {/* Liabilities */}
            <div>
              <h3 className="font-semibold text-foreground mb-3">
                LIABILITIES
              </h3>
              <div className="space-y-2">
                {liabilityAccounts.map((account) => (
                  <div
                    key={account.account_code}
                    className="flex justify-between text-sm"
                  >
                    <span className="text-muted-foreground">
                      {account.account_name}
                    </span>
                    <span>{formatCurrency(-Number(account.net_balance))}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Prepayment Pool</span>
                  <span>
                    {formatCurrency(balanceSheet.prepayment_pool_balance ?? 0)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between font-semibold mt-3 pt-2 border-t">
                <span>Total Liabilities</span>
                <span>
                  {formatCurrency(balanceSheet.totals.total_liabilities)}
                </span>
              </div>
            </div>

            {/* Equity */}
            <div>
              <h3 className="font-semibold text-foreground mb-3">EQUITY</h3>
              <div className="space-y-2">
                {equityAccounts.map((account) => (
                  <div
                    key={account.account_code}
                    className="flex justify-between text-sm"
                  >
                    <span className="text-muted-foreground">
                      {account.account_name}
                    </span>
                    <span>{formatCurrency(-Number(account.net_balance))}</span>
                  </div>
                ))}
                {/* Net Income (Current Year) */}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground italic">
                    Net Income (Current Year)
                  </span>
                  <span className="italic">{formatCurrency(netIncome)}</span>
                </div>
              </div>
              <div className="flex justify-between font-semibold mt-3 pt-2 border-t">
                <span>Total Equity</span>
                <span>{formatCurrency(totalEquityWithIncome)}</span>
              </div>
            </div>

            {/* Total Liabilities & Equity */}
            <div className="flex justify-between font-semibold pt-2 border-t-2">
              <span>Total Liabilities & Equity</span>
              <span>{formatCurrency(totalLiabilitiesAndEquity)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
